import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { api, money, date } from '../../lib/backofficeApi';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useTheme } from '../../context/ThemeContext';
import { 
  CreditCard, Check, Clock, Crown, Zap, Building2, 
  CheckCircle, AlertCircle, Loader2, ExternalLink
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const Subscription = () => {
  const [clinic, setClinic] = useState(null);
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const { theme } = useTheme();

  // Theme classes
  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-500';

  const loadData = useCallback(async () => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const [clinicRes, plansRes] = await Promise.all([
        axios.get(`${API}/clinic/${clinicId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/subscription/plans`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      let live = null;
      try { live = (await axios.get(`${API}/clinic/${clinicId}/billing/status`, { headers: { Authorization: `Bearer ${token}` } })).data; } catch {}
      setBilling(live);
      setClinic({ ...clinicRes.data, ...(live?.status ? { subscription_status: live.status, subscription_plan: live.plan } : {}) });
      setPlans(plansRes.data.plans);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPaymentStatus = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const cancelled = params.get('cancelled');
    if (params.get('checkout') === 'completed') {
      setPaymentStatus({ type: 'checking', message: 'Contratación recibida. El estado se actualizará al verificar el cobro con Stripe.' });
      loadData();
    }
    const token = localStorage.getItem('token');

    if (cancelled) {
      setPaymentStatus({ type: 'cancelled', message: 'Pago cancelado' });
      window.history.replaceState({}, '', '/dashboard/subscription');
      return;
    }

    if (sessionId) {
      setPaymentStatus({ type: 'checking', message: 'Verificando pago...' });
      
      // Poll for payment status
      const maxAttempts = 5;
      for (let i = 0; i < maxAttempts; i++) {
        try {
          const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.payment_status === 'paid') {
            setPaymentStatus({ type: 'success', message: '¡Pago exitoso! Tu suscripción está activa.' });
            loadData(); // Reload to get updated subscription status
            break;
          } else if (i === maxAttempts - 1) {
            setPaymentStatus({ type: 'error', message: 'No se pudo confirmar el pago. Contacta soporte si ya fue cobrado.' });
          }
          
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (error) {
          console.error('Error checking status:', error);
        }
      }
      
      window.history.replaceState({}, '', '/dashboard/subscription');
    }
  }, [loadData]);

  useEffect(() => {
    loadData();
    checkPaymentStatus();
  }, [loadData, checkPaymentStatus]);

  const handleSubscribe = async (planId) => {
    setProcessing(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/subscription/checkout/${clinicId}?plan=${planId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      setPaymentStatus({ type: 'error', message: error.response?.data?.detail || 'No se pudo preparar el enlace de contratación.' });
    } finally {
      setProcessing(false);
    }
  };

  const openBillingPortal = async () => {
    setProcessing(true);
    try { const r = await api(`/clinic/${localStorage.getItem('clinicId')}/billing/portal`, {}); window.location.href = r.url; }
    catch (e) { setPaymentStatus({ type: 'error', message: e.message }); }
    finally { setProcessing(false); }
  };

  const getTrialDaysLeft = () => {
    if (!clinic?.trial_ends_at) return 14;
    const trialEnd = new Date(clinic.trial_ends_at);
    const now = new Date();
    const diff = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const planIcons = {
    basico: Zap,
    profesional: Crown,
    empresarial: Building2
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className={`text-2xl font-bold ${textPrimary} flex items-center gap-2`}>
            <CreditCard className="w-6 h-6" />
            Suscripción
          </h1>
          <p className={`${textSecondary} mt-1`}>Administra tu plan y facturación</p>
        </div>

        {/* Payment Status Alert */}
        {paymentStatus && (
          <div className={`p-4 rounded-lg flex items-center gap-3 ${
            paymentStatus.type === 'success' ? 'bg-green-500/20 border border-green-500/30' :
            paymentStatus.type === 'error' ? 'bg-red-500/20 border border-red-500/30' :
            paymentStatus.type === 'cancelled' ? 'bg-yellow-500/20 border border-yellow-500/30' :
            'bg-blue-500/20 border border-blue-500/30'
          }`}>
            {paymentStatus.type === 'success' && <CheckCircle className="w-5 h-5 text-green-500" />}
            {paymentStatus.type === 'error' && <AlertCircle className="w-5 h-5 text-red-500" />}
            {paymentStatus.type === 'cancelled' && <AlertCircle className="w-5 h-5 text-yellow-500" />}
            {paymentStatus.type === 'checking' && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />}
            <span className={`${
              paymentStatus.type === 'success' ? 'text-green-600 dark:text-green-300' :
              paymentStatus.type === 'error' ? 'text-red-600 dark:text-red-300' :
              paymentStatus.type === 'cancelled' ? 'text-yellow-600 dark:text-yellow-300' :
              'text-blue-600 dark:text-blue-300'
            }`}>{paymentStatus.message}</span>
          </div>
        )}

        {/* Current Status */}
        <Card className={cardBg}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  clinic?.subscription_status === 'active' ? 'bg-green-500/20' :
                  clinic?.subscription_status === 'trial' ? 'bg-yellow-500/20' : 'bg-gray-500/20'
                }`}>
                  {clinic?.subscription_status === 'active' ? 
                    <CheckCircle className="w-6 h-6 text-green-500" /> :
                    <Clock className="w-6 h-6 text-yellow-500" />
                  }
                </div>
                <div>
                  <p className={`${textPrimary} font-medium`}>
                    {clinic?.subscription_status === 'active' ? 'Suscripción Activa' :
                     clinic?.subscription_status === 'trial' ? 'Período de Prueba' : 'Sin suscripción'}
                  </p>
                  <p className={`${textSecondary} text-sm`}>
                    {clinic?.subscription_status === 'trial' 
                      ? `${getTrialDaysLeft()} días restantes de prueba`
                      : `Plan ${clinic?.subscription_plan || 'starter'}`}
                  </p>
                </div>
              </div>
              {clinic?.subscription_status === 'active' && (
                <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-sm">
                  Activo
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {billing?.has_customer && <Card className={cardBg}><CardContent className="p-6 space-y-3"><h2 className={textPrimary}>Facturación y método de pago</h2><p className={textSecondary}>Mensualidad: {money(billing.subscription?.monthly_amount, billing.subscription?.currency || 'MXN')} · Vencimiento: {date(billing.subscription?.current_period_end)}</p>{billing.subscription?.payment_method && <p className={textSecondary}>{billing.subscription.payment_method.brand || billing.subscription.payment_method.type} ···· {billing.subscription.payment_method.last4}</p>}<Button onClick={openBillingPortal} disabled={processing}><ExternalLink className="w-4 h-4 mr-2" />Administrar facturación en Stripe</Button><p className={`text-xs ${textMuted}`}>Consulta tus comprobantes y las opciones habilitadas para tu contrato.</p></CardContent></Card>}

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans && Object.entries(plans).map(([planId, plan]) => {
            const Icon = planIcons[planId] || Zap;
            const isCurrentPlan = clinic?.subscription_plan === planId && clinic?.subscription_status === 'active';
            const isPopular = planId === 'profesional';

            return (
              <Card 
                key={planId} 
                className={`relative ${cardBg} ${
                  isPopular ? 'ring-2 ring-blue-500' : ''
                } ${isCurrentPlan ? 'ring-2 ring-green-500' : ''}`}
              >
                {isPopular && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs rounded-full">
                    Más popular
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-white text-xs rounded-full">
                    Plan actual
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 ${
                    planId === 'basico' ? 'bg-blue-500/20' :
                    planId === 'profesional' ? 'bg-purple-500/20' : 'bg-orange-500/20'
                  }`}>
                    <Icon className={`w-7 h-7 ${
                      planId === 'basico' ? 'text-blue-500' :
                      planId === 'profesional' ? 'text-purple-500' : 'text-orange-500'
                    }`} />
                  </div>
                  <CardTitle className={textPrimary}>{plan.name}</CardTitle>
                  <CardDescription className={textSecondary}>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className={`text-4xl font-bold ${textPrimary}`}>${plan.price.toLocaleString('es-MX')}</span>
                    <span className={textSecondary}> MXN/mes</span>
                  </div>
                  <p className={`text-xs ${textMuted} mt-1`}>
                    {plan.max_users} usuarios • {plan.max_conversations === -1 ? 'Conversaciones ilimitadas' : `${plan.max_conversations} conversaciones/mes`}
                  </p>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {plan.features && plan.features.map((feature, idx) => (
                      <li key={idx} className={`flex items-center gap-2 ${textSecondary}`}>
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button
                    onClick={() => billing?.subscription && !['canceled', 'incomplete_expired'].includes(billing.subscription.status) ? openBillingPortal() : handleSubscribe(planId)}
                    disabled={processing || isCurrentPlan}
                    className={`w-full text-white ${
                      isCurrentPlan ? 'bg-green-600 cursor-not-allowed' :
                      planId === 'profesional' ? 'bg-purple-600 hover:bg-purple-700' :
                      'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isCurrentPlan ? (
                      'Plan actual'
                    ) : clinic?.subscription_status === 'trial' ? (
                      'Activar plan'
                    ) : (
                      'Cambiar plan'
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ */}
        <Card className={cardBg}>
          <CardHeader>
            <CardTitle className={textPrimary}>Preguntas frecuentes</CardTitle>
          </CardHeader>
          <CardContent className={`space-y-4 ${textSecondary}`}>
            <div>
              <p className={`font-medium ${textPrimary}`}>¿Qué pasa cuando termina mi prueba gratuita?</p>
              <p className={`text-sm ${textSecondary} mt-1`}>
                Después de 14 días, necesitarás elegir un plan para continuar usando AI Med Planner. 
                Tus datos se conservan por 30 días adicionales.
              </p>
            </div>
            <div>
              <p className={`font-medium ${textPrimary}`}>¿Puedo cancelar en cualquier momento?</p>
              <p className={`text-sm ${textSecondary} mt-1`}>
                Sí, puedes cancelar tu suscripción cuando quieras. Seguirás teniendo acceso hasta el final 
                del período de facturación.
              </p>
            </div>
            <div>
              <p className={`font-medium ${textPrimary}`}>¿Qué métodos de pago aceptan?</p>
              <p className={`text-sm ${textSecondary} mt-1`}>
                Aceptamos tarjetas de crédito y débito (Visa, Mastercard, American Express) a través de Stripe.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Subscription;

